import styled from '@emotion/native';
import {
  CommonActions,
  createNavigatorFactory,
  type DefaultNavigatorOptions,
  type ParamListBase,
  type TabActionHelpers,
  type TabNavigationState,
  TabRouter,
  type TabRouterOptions,
  useNavigationBuilder,
} from '@react-navigation/native';
import { Col, Row } from 'components/base';
import { type IconName, Visible } from 'design-system/base';
import { BORDER_WIDTH, PADDING } from 'design-system/theme';
import { useUserSession } from 'hooks';
import type { LinkTo } from 'navigation';
import * as React from 'react';
import { Platform, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useBottomNavigatorContext } from './BottomNavigatorContext';
import { Tab } from './Tab';
import { TopBar } from './TopBar';

// Props accepted by the view
type TabNavigationConfig = {
  name?: string;
  initials?: string;
};

// Supported screen options
type TabNavigationOptions = {
  icon: IconName;
  link?: LinkTo;
  isHidden?: boolean;
};

// Map of event name and the type of data (in event.data)
//
// canPreventDefault: true adds the defaultPrevented property to the
// emitted events.
type TabNavigationEventMap = {
  tabPress: {
    data: { isAlreadyFocused: boolean };
    canPreventDefault: true;
  };
};

// The props accepted by the component is a combination of 3 things
type Props = DefaultNavigatorOptions<
  ParamListBase,
  string | undefined,
  TabNavigationState<ParamListBase>,
  TabNavigationOptions,
  TabNavigationEventMap,
  unknown
> &
  TabRouterOptions &
  TabNavigationConfig;

const Screen = styled.View(({ theme }) => {
  const { height } = useWindowDimensions();
  const { top } = useSafeAreaInsets();

  const iosScreenHeight = height - top;
  const screenHeight = Platform.OS === 'ios' ? iosScreenHeight : height;

  return {
    height: screenHeight,
    backgroundColor: theme.color.base,
  };
});

const BottomBar = styled(Row)(({ theme }) => () => {
  const { bottom } = useSafeAreaInsets();

  const bottomBarHeight = Platform.OS === 'ios' ? bottom : 0;

  return {
    paddingBottom: bottomBarHeight,
    backgroundColor: theme.color['base-grey'],
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingHorizontal: PADDING.s,
    maxWidth: '100%',
    borderStyle: 'solid',
    borderWidth: 0,
    borderColor: theme.color.divider,
    borderTopWidth: BORDER_WIDTH[1],
  };
});

const Page = styled(Col)({
  flex: 1,
});

/**
 * Web-style Top Navigator implemented as per the
 * [React-Navigation Documentation Sample](https://reactnavigation.org/docs/custom-navigators/)
 */
export const BottomNavigator = ({
  initialRouteName,
  children,
  screenOptions,
  name,
}: Props): JSX.Element => {
  const { state, navigation, descriptors, NavigationContent } =
    useNavigationBuilder<
      TabNavigationState<ParamListBase>,
      TabRouterOptions,
      TabActionHelpers<ParamListBase>,
      TabNavigationOptions,
      TabNavigationEventMap
    >(TabRouter, {
      children,
      screenOptions,
      initialRouteName,
    });

  const { isNavigationVisible } = useBottomNavigatorContext();
  const session = useUserSession();

  const navigateToProfile = React.useCallback(() => {
    navigation.navigate('Profile');
  }, [navigation]);

  return (
    <NavigationContent>
      <Screen>
        <Page unsetZIndex>
          <Visible if={isNavigationVisible}>
            <TopBar
              name={name}
              onProfilePress={navigateToProfile}
              wards={session.wards}
              onWardSelect={session.selectWard}
              ward={session.ward}
            />
          </Visible>
          {state.routes.map((route, index) => {
            const isVisible = index === state.index;

            if (!isVisible) {
              return null;
            }

            return descriptors[route.key].render();
          })}
        </Page>

        <Visible if={isNavigationVisible}>
          <BottomBar unsetZIndex>
            {state.routes.map((route, index) => {
              const { icon, link, isHidden } = descriptors[route.key].options;

              if (isHidden) {
                return null;
              }

              const isActive = state.index === index;

              const isAlreadyFocused =
                route.key === state.routes[state.index].key;

              const onPress = () => {
                if (link && typeof link !== 'string') {
                  navigation.navigate(link.screen, link.params);
                  return;
                }

                const event = navigation.emit({
                  type: 'tabPress',
                  target: route.key,
                  canPreventDefault: true,
                  data: {
                    isAlreadyFocused,
                  },
                });

                if (!event.defaultPrevented) {
                  navigation.dispatch({
                    ...CommonActions.navigate({
                      name: route.name,
                      merge: true,
                    }),
                    target: state.key,
                  });
                }
              };

              return (
                <Tab
                  key={route.key}
                  onPress={onPress}
                  icon={icon}
                  isActive={!!isActive}
                />
              );
            })}
          </BottomBar>
        </Visible>
      </Screen>
    </NavigationContent>
  );
};

export const createBottomNavigator = createNavigatorFactory(BottomNavigator);
